import { ButtonProps, ButtonType, DialogFooterButtonsWidget, TextLinkWidget, TextLinkKind } from '@tableau/widgets';
import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { Messages } from '../../codegen/Localize';
import { IContentInfo } from '../../ts/models/IContentInfo';
import { IDataSourceBase } from '../../ts/models/IDataSourceInfo';
import { shareContent } from '../actions/spaces/ShareContentAction';
import { ShareContentButton, ShareContentButtonProps } from '../spaces/ShareContent/ShareContentButton';
import { MapDispatchToProps, MapStateToProps } from '../utils/ConnectUtils';

export interface PublishCompleteDialogOwnProps {
  dismiss: () => void;
  close: () => void;
  onDevicePreviewClick: () => void;
  workbook: IContentInfo<'workbook'>;
  datasources: IDataSourceBase[];
  getDatasourceIconClass: (datasource: IDataSourceBase) => string;
  publishLiveQueryInfo: string;
  shouldDisplayDevicePreview: boolean;
  shouldDisplayLiveQueryInfo: boolean;
  shouldDisplayScheduleButton: boolean;
}

export interface PublishCompleteDialogDispatchProps {
  readonly shareContent: (content: IContentInfo) => void;
}

export type PublishCompleteDialogProps = PublishCompleteDialogOwnProps & PublishCompleteDialogDispatchProps;


interface DatasourceProps {
  name: string;
  datasourceIconClass: string;
}

export namespace PublishCompleteDialog {
  export function Title(): string {
    return Messages.publishDatasourceSuccessDialogTitleComplete();
  }

  export function Content(props: PublishCompleteDialogProps) {
    return (
      <div className="tb-workbook-publish-success-modal-with-preview">
        <div className="tb-dialog-body-text">
          <div className="tb-published-items">
            <WorkbookSection {...props} />
            <DatasourceSection {...props} />
          </div>
          <TableauBridgeBox {...props}/>
        </div>
      </div>
    );
  }

  export function WorkbookSection(props: PublishCompleteDialogProps) {
    return (
      <div className="tb-workbook">
        <div className="tb-workbook-body">
          <div className="tb-published-item">
            <div className="tb-workbook-wrapper">
              <div className="tb-published-icon tb-dialogs-workbook" />
              <div className="tb-published-name">{props.workbook.name}</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  export function DevicePreviewLink(props: PublishCompleteDialogProps) {
    if (!props.shouldDisplayDevicePreview) {
      return null;
    }

    return (
      <div className="tb-device-preview">
        <TextLinkWidget
          testId={'tb-device-preview-text'}
          className={'tb-device-preview-text'}
          onClick={props.onDevicePreviewClick}
          kind={TextLinkKind.LowEmphasis}
          tabIndex={0}
        >
          {Messages.publishWorkbookSuccessDialogText_devicePreviewMessage()}
        </TextLinkWidget>
      </div>
    );
  }

  export function DatasourceSection(props: PublishCompleteDialogProps) {
    if (props.datasources.length === 0 && props.shouldDisplayScheduleButton === false) {
      return null;
    }

    return (
      <div className="tb-datasources">
        <span className="tb-datasources-title">{Messages.publishWorkbookSuccessDialogText_datasourcesHeader()}</span>
        <div className="tb-datasources-body">
          {props.datasources.map((item, index) => <Datasource key={index} {...{name: item.name, datasourceIconClass: props.getDatasourceIconClass(item)}}/>)}
          <ScheduleRefreshLink {...props}/>
        </div>
      </div>
    );
  }

  export function Datasource(props: DatasourceProps): JSX.Element {
    const iconClass = `tb-published-icon ${props.datasourceIconClass}`;
    return (
      <div className="tb-published-item">
        <div className="tb-datasource-wrapper">
          <div className={iconClass} />
          <div className="tb-published-name">{props.name}</div>
        </div>
      </div>
    );
  }

  export function ScheduleRefreshLink(props: PublishCompleteDialogProps) {
    if (props.shouldDisplayScheduleButton === false) {
      return null;
    }

    return (
      <div className="tb-data-refresh">
        <div className="tb-data-refresh-text">{Messages.publishWorkbookSuccessDialogText_scheduleMessage()}</div>
        <a className="tb-data-refresh-link" onClick={props.close}>{Messages.publishWorkbookSuccessDialogText_scheduleLink()}</a>
      </div>
    );
  }

  export function TableauBridgeBox(props: PublishCompleteDialogProps) {
    if (!props.shouldDisplayLiveQueryInfo) {
      return null;
    }

    return (
      <div className="tb-box tb-publish-details">
        <span>{props.publishLiveQueryInfo}</span>
        &nbsp;
        <span
            tb-popover="'datasourcePublishSuccessWhatIsSync.html'"
            tb-popover-class="tb-popover-wrapper-short-height"
            tb-popover-delay="150"
            className="tb-datasource-publish-details-popover-trigger">
          {Messages.publishDatasourceSuccessDialogText_onlineSync_whatsthis()}
        </span>
      </div>
    );
  }

  function Footer(props: PublishCompleteDialogProps) {
    const ShareContentButtonProps: ShareContentButtonProps = {
      handleClick: () => {
        props.close();
        props.shareContent(props.workbook);
      },
      text: Messages.publishWorkbookSuccessDialogButton_share(),
      testId: 'share-icon'
    };

    return <div
      className="publish-complete-dialog-footer"
      data-tb-test-id="publish-complete-dialog-footer"
      >
      <DialogFooterButtonsWidget buttons={[ ShareContentButton(ShareContentButtonProps) ]} testId="publish-complete-dialog-footer-buttons" />
      <DevicePreviewLink {...props} />
    </div>;
  }

  const mapDispatchToProps: MapDispatchToProps<PublishCompleteDialogDispatchProps, PublishCompleteDialogOwnProps> = (dispatch) => bindActionCreators({
    shareContent: shareContent,
  }, dispatch);

  export const ConnectedFooter: React.ComponentClass<PublishCompleteDialogOwnProps> = connect(null, mapDispatchToProps)(Footer);
}
